import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Building2, ClipboardList, Power, Users } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";
import { getCompanies, getCompaniesDetails, getSuperStats, toggleCompanyActive } from "../../lib/metalApi";

const EASE = [0.16, 1, 0.3, 1];

export const AziendeTab = () => {
  const { lang, t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setError("");
    setLoading(true);
    try {
      // Prima la lista (endpoint veloce): la schermata appare subito
      const c = await getCompanies();
      const list = Array.isArray(c) ? c : c.items ?? [];
      setCompanies(list);
      setLoading(false);
      // Statistiche in arrivo appena pronte
      getSuperStats()
        .then((s) => s && setStats(s))
        .catch(() =>
          setStats({
            total_companies: list.length,
            active_companies: list.filter((x) => x.is_active).length,
            total_users: list.reduce((sum, x) => sum + (x.user_count || 0), 0),
            total_classifications: list.reduce((sum, x) => sum + (x.classification_count || 0), 0),
          })
        );
      // Arricchimento in background: utenti e conteggi per azienda (endpoint lento)
      getCompaniesDetails()
        .then((details) => {
          if (!Array.isArray(details)) return;
          setCompanies((prev) =>
            prev.map((comp) => {
              const d = details.find((x) => x.id === comp.id);
              return d ? { ...comp, users: d.users, classification_count: d.classification_count } : comp;
            })
          );
        })
        .catch(() => {});
    } catch (e) {
      setError(e?.message || "network_error");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onToggle = async (id) => {
    if (!window.confirm(t.reserved.confirmToggle)) return;
    try {
      await toggleCompanyActive(id);
      await load();
    } catch {
      toast.error(t.reserved.errorGeneric);
    }
  };

  if (loading) {
    return (
      <div data-testid="aziende-loading" className="flex justify-center py-16">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#34D399] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <p
        data-testid="aziende-error"
        className="rounded-2xl border border-[#F87171]/40 bg-[#F87171]/10 px-4 py-3 font-manrope text-sm text-[#F87171]"
      >
        {t.reserved.dataError} <span className="opacity-70">({error})</span>
      </p>
    );
  }

  const statCards = stats
    ? [
        { label: t.reserved.totalCompanies, value: stats.total_companies, icon: Building2 },
        { label: t.reserved.activeCompanies, value: stats.active_companies, icon: Power },
        { label: t.reserved.totalUsers, value: stats.total_users, icon: Users },
        { label: t.reserved.totalClassifications, value: stats.total_classifications, icon: ClipboardList },
      ]
    : [];

  return (
    <div data-testid="aziende-tab" className="space-y-5">
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {statCards.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05, ease: EASE }}
              data-testid={`aziende-stat-${i}`}
              className="rounded-2xl border border-[#342C55] bg-[#221D3F] p-4"
            >
              <s.icon className="h-4 w-4 text-[#34D399]" />
              <p className="mt-2 font-bebas text-3xl text-[#F5F5F5]">{s.value}</p>
              <p className="font-manrope text-[10px] uppercase tracking-[0.12em] text-[#8B87A8]">{s.label}</p>
            </motion.div>
          ))}
        </div>
      )}

      {companies.map((c, i) => (
        <motion.div
          key={c.id ?? i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
          data-testid={`azienda-card-${i}`}
          className="rounded-2xl border border-[#342C55] bg-[#221D3F] p-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <p className="font-manrope text-base font-bold text-[#F5F5F5]">{c.name}</p>
                <span
                  className={`rounded-full px-2.5 py-0.5 font-manrope text-[10px] font-bold uppercase tracking-wide ${
                    c.is_active ? "bg-[#34D399]/15 text-[#34D399]" : "bg-[#F87171]/15 text-[#F87171]"
                  }`}
                >
                  {c.is_active ? t.reserved.activeLabel : t.reserved.inactiveLabel}
                </span>
              </div>
              <p className="mt-1 font-manrope text-xs text-[#8B87A8]">
                {t.reserved.companyCode}: <span className="font-bold text-[#34D399]">{c.code}</span>
                {" · "}
                {c.email_classifiche}
                {" · "}
                {t.reserved.since}{" "}
                {new Date(c.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
            <button
              data-testid={`azienda-toggle-${i}`}
              onClick={() => onToggle(c.id)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 font-manrope text-xs font-bold transition-colors duration-300 ${
                c.is_active
                  ? "border border-[#F87171]/50 text-[#F87171] hover:bg-[#F87171]/10"
                  : "bg-[#34D399] text-[#17122E] hover:bg-[#2BBD8C]"
              }`}
            >
              <Power className="h-3.5 w-3.5" />
              {c.is_active ? t.reserved.deactivate : t.reserved.activate}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-4 border-t border-[#342C55] pt-4">
            <span className="font-manrope text-xs text-[#8B87A8]">
              <span className="font-bold text-[#F5F5F5]">{c.user_count}</span> {t.reserved.usersLabel}
            </span>
            <span className="font-manrope text-xs text-[#8B87A8]">
              <span className="font-bold text-[#F5F5F5]">{c.classification_count ?? "–"}</span>{" "}
              {t.reserved.classificationsLabel}
            </span>
          </div>

          {c.users?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {c.users.map((u, j) => (
                <span
                  key={u.id ?? j}
                  data-testid={`azienda-user-${i}-${j}`}
                  className="rounded-full border border-[#342C55] px-3 py-1 font-manrope text-[11px] text-[#8B87A8]"
                >
                  {u.username}
                  <span className="ml-1.5 text-[#34D399]">{u.classification_count}</span>
                </span>
              ))}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
};
